import { MOUNT_READY_MARKER, whenMountReady } from "#ce/framework/mount-ready";
import { resolveActivatorControl } from "#ce/utils/activator-control";
import { deepActiveElement } from "#ce/utils/focus";

// Runs `focusTarget` once `element` is mount-ready, unless focus has moved from `focusedBeforeWait`.
function focusWhenMountReady(element, focusedBeforeWait, focusTarget) {
  whenMountReady(element).then(
    () => {
      if (deepActiveElement() === focusedBeforeWait) {
        focusTarget();
      }
    },
    () => {},
  );
}

// The activator, or a custom element inside it, that has not mounted yet.
function pendingCustomElement(activator) {
  return [activator, ...activator.querySelectorAll("*")].find(
    (element) => element.localName.includes("-") && !element.hasAttribute(MOUNT_READY_MARKER),
  );
}

/**
 * Returns `focus`, which focuses the select's activator: a slotted activator's control, or else
 * the field's input, which sits in the field's shadow root. A slotted activator holding an `sp-*`
 * element, or a field, whose definition has not arrived yet takes the focus once it is mount-ready,
 * unless focus has moved meanwhile.
 *
 * @param {{ hostElement: HTMLElement | null | undefined,
 *   field: import("vue").Ref<HTMLElement & { inputElement?: HTMLElement } | null> }} options
 * @returns {{ focus: (options?: FocusOptions) => void }}
 */
export function useActivatorFocus({ hostElement, field }) {
  // Waits for each element of the activator that has not mounted yet, until its control resolves.
  function focusSlottedActivator(slotted, options, focusedBeforeWait = deepActiveElement()) {
    const control = resolveActivatorControl(slotted);
    const pending = control ? null : pendingCustomElement(slotted);

    if (!pending) {
      control?.focus(options);
      return;
    }

    focusWhenMountReady(pending, focusedBeforeWait, () => focusSlottedActivator(slotted, options, focusedBeforeWait));
  }

  function focus(options) {
    const slotted = hostElement?.querySelector(':scope > [slot="activator"]');

    if (slotted) {
      focusSlottedActivator(slotted, options);
      return;
    }

    const fieldElement = field.value;

    if (!fieldElement || fieldElement.inputElement) {
      fieldElement?.inputElement?.focus(options);
      return;
    }

    focusWhenMountReady(fieldElement, deepActiveElement(), () => fieldElement.inputElement?.focus(options));
  }

  return { focus };
}
